import { Printer } from "lucide-react";

import type {
  AutoPrint,
  PrintType,
} from "~/components/stations/station-edit-types";
import {
  isAutoPrint,
  isPrintType,
} from "~/components/stations/station-edit-types";
import { Checkbox } from "~/components/ui/checkbox";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { parseEnumValue } from "~/lib/parse-enum-value";

interface PrinterSettingsSectionProps {
  printersAvailable: { value: string; label: string }[];
  selectedPrinterIds: string[];
  setSelectedPrinterIds: (ids: string[]) => void;
  autoPrint: AutoPrint;
  setAutoPrint: (value: AutoPrint) => void;
  printType: PrintType;
  setPrintType: (value: PrintType) => void;
  printOrderTypes: string[];
  setPrintOrderTypes: (types: string[]) => void;
}

export function PrinterSettingsSection({
  printersAvailable,
  selectedPrinterIds,
  setSelectedPrinterIds,
  autoPrint,
  setAutoPrint,
  printType,
  setPrintType,
  printOrderTypes,
  setPrintOrderTypes,
}: PrinterSettingsSectionProps) {
  const togglePrinter = (printerId: string, checked: boolean) => {
    if (checked) {
      setSelectedPrinterIds([...selectedPrinterIds, printerId]);
    } else {
      setSelectedPrinterIds(
        selectedPrinterIds.filter((id) => id !== printerId)
      );
    }
  };

  const toggleOrderType = (orderType: string, checked: boolean) => {
    if (checked) {
      setPrintOrderTypes([...printOrderTypes, orderType]);
    } else {
      setPrintOrderTypes(printOrderTypes.filter((t) => t !== orderType));
    }
  };

  return (
    <>
      <div className="flex flex-col gap-2">
        <Label>
          Printers{" "}
          {selectedPrinterIds.length > 0 && (
            <span className="text-muted-foreground">
              ({selectedPrinterIds.length} selected)
            </span>
          )}
        </Label>
        {printersAvailable.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            No printers configured. Add printers in Settings &rarr; Printers.
          </p>
        ) : (
          <div className="border-border space-y-2 rounded-lg border p-3">
            {printersAvailable.map((printer) => {
              const isSelected = selectedPrinterIds.includes(printer.value);
              const checkboxId = `printer-${printer.value}`;
              return (
                <label
                  className="hover:bg-accent flex cursor-pointer items-center gap-3 rounded-lg px-2 py-1.5"
                  htmlFor={checkboxId}
                  key={printer.value}
                >
                  <Checkbox
                    checked={isSelected}
                    id={checkboxId}
                    onCheckedChange={(checked) =>
                      togglePrinter(printer.value, !!checked)
                    }
                  />
                  <div className="flex items-center gap-2">
                    <Printer className="text-muted-foreground h-4 w-4" />
                    <span>{printer.label}</span>
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </div>

      {selectedPrinterIds.length > 0 && (
        <div className="space-y-4">
          <div className="flex flex-col gap-2">
            <Label>Auto-Print</Label>
            <Select
              onValueChange={(v) =>
                setAutoPrint(parseEnumValue(v, isAutoPrint, "off"))
              }
              value={autoPrint}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="off">Off - Manual printing only</SelectItem>
                <SelectItem value="onReceived">
                  On Received - Print when order arrives
                </SelectItem>
                <SelectItem value="onBump">
                  On Bump - Print when bumped on KDS
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <Label>Print Format</Label>
            <Select
              onValueChange={(v) =>
                setPrintType(parseEnumValue(v, isPrintType, "ticket"))
              }
              value={printType}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ticket">
                  Ticket - Standard kitchen ticket
                </SelectItem>
                <SelectItem value="label">
                  Label - Compact label format
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <Label>Print for Order Types</Label>
            <p className="text-muted-foreground text-xs">
              Select which order types should trigger printing
            </p>
            <div className="flex flex-wrap gap-3">
              {[
                { value: "dineIn", label: "Dine In" },
                { value: "takeout", label: "Takeout" },
                { value: "delivery", label: "Delivery" },
              ].map((orderType) => {
                const checkboxId = `order-type-${orderType.value}`;
                return (
                  <label
                    className="border-border hover:bg-accent flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2"
                    htmlFor={checkboxId}
                    key={orderType.value}
                  >
                    <Checkbox
                      checked={printOrderTypes.includes(orderType.value)}
                      id={checkboxId}
                      onCheckedChange={(checked) =>
                        toggleOrderType(orderType.value, !!checked)
                      }
                    />
                    <span className="text-sm">{orderType.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
